// src/hooks/results/mapMeasurementRow.js
import {
  pickHrSignalQuality,
  pickRrSignalQuality,
  pickRr,
} from "../../utils/results/signalQuality";
import {
  pickHeartDxHRV,
  pickHeartDxHrBpm,
} from "../../utils/results/heartdx";
import {
  formatRecordingIdDateParts,
  parseTimestampFromRecordingId,
} from "../../utils/results/recordingIdTime";
import { isUploadFailedDoc } from "../../utils/results/isUploadFailedDoc";
import { isManualAudioCompleteDoc } from "../../utils/results/manualAudioStatus";
import {
  pickPostExertionRrAvg,
  pickPostExertionRrMax,
  pickPostExertionRrMin,
} from "../../utils/results/postExertion";
import { buildSnapshotSubset } from "./readSnapshotField";
import { getSuppressedMetricKeys } from "../../utils/results/processingOutcomes";

const MEASUREMENT_LIST_FIELDS = [
  "appMode",
  "upload",
  "status",
  "createdAt",
  "takenAt",
  "submittedAt",
  "completedAt",
  "audioPath",
  "audioDownloadURL",
  "videoPath",
  "videoDownloadURL",
  "metadataPath",
  "hr",
  "heartdx.hr",
  "heartdx.hrv.SDNN",
  "heartdx.ppg_signal_quality",
  "rppg_signal_quality",
  "ppg_signal_quality",
  "signal_quality_text",
  "signalQualityText",
  "signal_quality_code",
  "results.heartdx_signal_quality",
  "results.heartdx_signal_quality_score",
  "results.heartdx.rppg_signal_quality",
  "results.heartdx.data.hr",
  "results.heartdx.data.hrv.SDNN",
  "results.heartdx.data.rppg_signal_quality",
  "results.heartdx.data.heartdx_signal_quality_score",
  "results.heartdx.data.sqi",
  "results.mpm.rr_avg",
  "results.mpm.rr_min",
  "results.mpm.rr_max",
  "results.mpm.quality",
  "results.mpm.respiratory_rate.rr_avg",
  "results.mpm.respiratory_rate.minBreathsPerMinute",
  "results.mpm.respiratory_rate.maxBreathsPerMinute",
  "results.mpm.respiratory_rate.metrics.rr_avg",
  "results.mpm.respiratory_rate.metrics.minBreathsPerMinute",
  "results.mpm.respiratory_rate.metrics.maxBreathsPerMinute",
  "results.mpm.respiratory_rate.upstreamQuality.status",
  "results.mpm.respiratory_rate.upstreamQuality.checks",
  "results.mpm.respiratory_rate.qualityStatus",
  "results.mpm.respiratory_rate.quality",
  "results.mpm_signal_quality.status",
  "results.mpm_signal_quality.checks",
  "results.mpm.mpm_signal_quality.status",
  "results.mpm.mpm_signal_quality.checks",
  "results.mpm.flow_loop.summary_metrics.meanExhaleInhaleAreaRatio",
  "results.Respiratory_rate",
  "results.respiratory_rate",
  "results.rgb_signal_quality",
  "results.processingOutcomes",
];

export function buildMeasurementListSummary(doc) {
  return buildSnapshotSubset(doc, MEASUREMENT_LIST_FIELDS);
}

/**
 * Canonical HR accessor after backend changes.
 * Prefer heartdx.hr / heartdx.hr_median; fall back to legacy fields.
 */
export function readHrBpm(data) {
  return pickHeartDxHrBpm(data);
}

export function readHrvMs(data) {
  return pickHeartDxHRV(data);
}

/**
 * RR accessor (your existing tidal breathing fields).
 */
export function readRrBpm(data) {
  return pickRr(data);
}

export function readSignalQuality(data, context) {
  // Respiratory: from `results` (pass/fail)
  if (context === "respiratory") {
    return readRrSignalQuality(data);
  }

  // Pulse: prefer your existing quality fields if present
  // (these were used in PPG results, keep them as-is)
  return (
    data?.signal_quality_text ||
    data?.signalQualityText ||
    data?.ppg_signal_quality ||
    data?.heartdx?.ppg_signal_quality ||
    null
  );
}

export function readRrSignalQuality(data) {
  return pickRrSignalQuality(data);
}

export function readHrSignalQuality(data) {
  // Reuse the same logic as the pulse scan result view
  return pickHrSignalQuality(data); // "very good" | "good" | "bad" | null
}

/**
 * Map a Firestore doc => UI row.
 * context: "pulse" | "respiratory" | "cardiopulmonary"
 */
export function mapMeasurementDocToRow(
  doc,
  context,
  languageFallback = "en-GB",
) {
  const data = buildMeasurementListSummary(doc);
  const resolvedContext = context || data?.appMode || "cardiopulmonary";
  const ts = parseTimestampFromRecordingId(doc.id);
  const { time, date } = formatRecordingIdDateParts(doc.id, languageFallback);

  const hr = readHrBpm(data);
  const hrv = readHrvMs(data);
  const rr = pickPostExertionRrAvg(data) ?? readRrBpm(data);
  const rrMin = pickPostExertionRrMin(data);
  const rrMax = pickPostExertionRrMax(data);
  const meanExhaleInhaleAreaRatio =
    typeof data?.results?.mpm?.flow_loop?.summary_metrics
      ?.meanExhaleInhaleAreaRatio === "number" &&
    Number.isFinite(data.results.mpm.flow_loop.summary_metrics.meanExhaleInhaleAreaRatio)
      ? data.results.mpm.flow_loop.summary_metrics.meanExhaleInhaleAreaRatio
      : null;

  // Per-metric quality (independent)
  const rrQuality = readRrSignalQuality(data); // "pass" | "fail" | null
  const hrQuality = readHrSignalQuality(data); // "very good" | "good" | "bad" | null
  const uploadFailed = isUploadFailedDoc(data);
  const manualAudioComplete = isManualAudioCompleteDoc(data);
  const suppressedMetrics = getSuppressedMetricKeys(data, ["hr", "hrv", "rr"]);

  const row = {
    id: doc.id,
    timestamp: ts ? ts.toISOString() : null,
    time,
    date,

    hr: hr != null ? Math.round(hr) : null,
    hrv,
    rr,
    rrMin,
    rrMax,
    meanExhaleInhaleAreaRatio,

    // NEW: expose quality per metric
    rrQuality,
    hrQuality,
    uploadFailed,
    manualAudioComplete,

    // keep old fields for anything still using them
    context: resolvedContext,
    signalQuality: readSignalQuality(data, resolvedContext),
    signalQualityCode: data?.signal_quality_code ?? null,
    ...(suppressedMetrics.length ? { suppressedMetrics } : {}),
  };

  ["createdAt", "takenAt", "submittedAt", "completedAt"].forEach((field) => {
    if (data?.[field] !== undefined && data?.[field] !== null) {
      row[field] = data[field];
    }
  });

  return row;
}
